<template lang="pug">
  .play-box
    template(v-if="!isLoading")
      game-header(
        @soundStateChanged="headerKey = !headerKey",
        :key="headerKey",
        :time="getTime",
        :slug="type",
        :game-type="parameters && parameters.courseType",
        :is-homework="!!exerciseId",
      )
      .play-box__game(
        v-if="!gameFinished && gameStarted"
      )
        .play-box__example
          .play-box__example-info-container(v-if="!isCountingCombinationsGameMode" )
            .play-box__search-number-container
              .play-box__header
                | {{ getTitleForSearchSequences }}:
              .game-info__container
                game-info(
                  :score="{left: goodAnswers, right: badAnswers}",
                  :parameters="parameters",
                  :game-allows-tips="true",
                  :is-need-to-use-default-position-for-tips-block="true"
                )
              .play-box__search-number-wrapper
                template(v-if="isNumberGameMode")
                  .play-box__search-number.play-box__search-number_default(
                    v-for="numericSequence in numericSequencesValuesForShow"
                    :style="searchNumberFontSize"
                  )
                    | {{ numericSequence.flat().join('') }}
                template(v-if="!isNumberGameMode")
                  .play_box__search-characters-sequence(
                    v-for="numericSequence in numericSequencesValuesForShow"
                    :style="searchNumberFontSize"
                  )
                    template(v-if="crossLines && crossLines.length && !gameFinished")
                      .cross-lines(
                        v-for="crossLine in crossLines"
                        :style="crossLine.styles"
                      )
                    .play-box__search-characters.play-box__search-number_default
                      sequence-of-numbers-table-icons(
                        v-for="numericSequenceValue in numericSequence"
                        :class-name="'cell-image'"
                        :width="isMobile ?  '20px' : '28px'"
                        :height="isMobile ? '20px' : '28px'"
                        :name="CELLS_ICONS_NAMES[numericSequenceValue]"
                        :fill="checkIsSequenceFounded(numericSequence) ? CORRECT_ANSWER_ORANGE_COLOR : DEFAULT_GREEN_COLOR"
                      )
          .answer(v-else)
            .predetermined-sequence-wrapper
              .predetermined-sequence
                sequence-of-numbers-table-icons.predetermined-sequence-character(
                  v-for="numericSequenceValue in predeterminedNumericSequenceValue"
                  :class-name="'cell-image'"
                  :width="isMobile ?  '20px' : '28px'"
                  :height="isMobile ? '20px' : '28px'"
                  :name="CELLS_ICONS_NAMES[numericSequenceValue]"
                  :fill="DEFAULT_GREEN_COLOR"
                )
            .answer-box
              label.answer-label
                | {{ $i18n.t('trainer.trainer_gamePlayBox_answer') }}:
              cs-input.answer-input(
                maxlength="3",
                @keydown.native.prevent.enter="checkIsAnswerCorrectForCountingCombinationsGameMode",
                v-model="answerForCountingCombinationsMode"
                @input.prevent.native="answerForCountingCombinationsMode = $event.target.value.replace(/[^\\d,.-]+/gm, '')"
                ref="input"
                v-focus="true"
              )
              cs-button.answer-button(
                color="blue",
                type="button",
                @click="checkIsAnswerCorrectForCountingCombinationsGameMode"
              )
                | {{ $i18n.t('trainer.trainer_gamePlayBox_reply') }}
          template(v-if="lines && lines.length && !gameFinished")
            .correct-lines(
              v-for="line in lines"
              :style="line.isCorrect && line.styles"
            )
          .drawing-lines(v-if="!gameFinished" ref="lines")
          sequence-of-numbers-table(
            :sequence-of-numbers-table="sequenceOfNumbersTable"
            @mousedown="mouseDown"
            @mousemove="mouseMove"
            :is-colored-cells="parameters && parameters.coloredNumbers"
            :is-numbers-mode="isNumberGameMode"
            :selected-lines-indexes="selectedLinesIndexes.flat()"
            :is-counting-combinations-game-mode="isCountingCombinationsGameMode"
          )
      .play-box__end-container(v-if="gameFinished")
        fireworks(
          v-if="gameFinished"
          :is-mobile="isMobile"
        )
        .play-box__reaction
          .play-box__reaction-side.play-box__reaction-side--correct
            div
              | {{ $i18n.t('trainer.trainer_gamePlayBox_correct') }}
            .play-box__reaction-count
              | {{ goodAnswers }}
          result-image.play-box__reaction-image(:status="'success'")
          .play-box__reaction-side.play-box__reaction-side--incorrect
            div
              | {{ $i18n.t('trainer.trainer_gamePlayBox_wrong') }}
            .play-box__reaction-count
              | {{ badAnswers }}
        result-buttons(
          :exercise-id="exerciseId"
          :games-type="parameters.courseType"
          show-restart-button
          @restartGame="restartGame"
        )
          slot
    cs-loader(v-else)
</template>

<script>
    import SequenceOfNumbersTable from './SequenceOfNumbersTable.vue';
    import { cloneDeep, chunk, isEqual } from 'lodash';
    import playBoxGameMixin from '../../../../utils/trainer/playBoxGameMixin.js';
    import EndGameMixin from '../../../../utils/trainer/endGame';
    import GameHeader from '../../GameHeader.vue';
    import GameInfo from '../GameInfo.vue';
    import Fireworks from '../Fireworks.vue';
    import ResultImage from '../ResultImage.vue';
    import SequenceOfNumbersTableIcons from './SequenceOfNumbersTableIcons';

    const CORRECT_ANSWER_ORANGE_COLOR = 'hsl(14, 100%, 64%)';
    const DEFAULT_GREEN_COLOR = 'hsl(121, 38%, 58%)';
    const PERMISSIBLE_ANGLES_OF_ROTATION = [
        '0deg',
        '45deg',
        '90deg',
        '135deg',
        '180deg',
        '-45deg',
        '-90deg',
        '-135deg',
        '-180deg',
    ];
    const CELLS_ICONS_NAMES = [
        'drop',
        'star',
        'circles',
        'circle',
        'smallCircle',
        'square',
        'rhombus',
        'squareRhombus',
        'emptySquare',
        'triangle',
    ];
    const NUMBERS_GAME_MODE = 'numbers';
    const DEFAULT_VOLUME = 0.6;
    const MIN_TABLE_CELL_VALUE = 0;
    const MAX_TABLE_CELL_VALUE = 9;
    const HORIZONTALLY_LOCATION = 'horizontally';
    const VERTICALLY_LOCATION = 'vertically';
    const DIAGONALLY_LOCATION = 'diagonally';
    const DIAGONALLY_LOCATION_TYPES = ['left', 'right'];
    const MAX_RETRIES_COUNT = 1000;

    function DEFAULT_DATA() {
        return {
            CORRECT_ANSWER_ORANGE_COLOR,
            DEFAULT_GREEN_COLOR,
            CELLS_ICONS_NAMES,
            sequenceOfNumbersTable: [],
            sequenceOfNumbersTableCells: [],
            amountOfNumbers: null,
            badAnswers: 0,
            goodAnswers: 0,
            prepareForGame: true,
            amountOfExamples: 0,
            headerKey: false,
            numericSequences: [],
            numericSequencesValues: [],
            availableCellsForCountingGameModeWithCrossing: [],
            unavailableCellsForCountingGameModeWithCrossing: [],
            verticallyNumericSequencesSettings: {
                uniqueColumnIndexes: [],
                unavailableCells: [],
                maxCount: null,
                amount: 0,
                availableCellsForCountingGameModeWithCrossing: [],
            },
            horizontallyNumericSequencesSettings: {
                uniqueRowIndexes: [],
                unavailableCells: [],
                maxCount: null,
                amount: 0,
                availableCellsForCountingGameModeWithCrossing: [],
            },
            diagonallyNumericSequencesSettings: {
                unavailableCells: [],
                left: {
                    uniqueCells: [],
                    maxCount: null,
                    amount: 0,
                    availableCellsForCountingGameModeWithCrossing: [],
                },
                right: {
                    uniqueCells: [],
                    maxCount: null,
                    amount: 0,
                    availableCellsForCountingGameModeWithCrossing: [],
                },
            },
            currentLineCoordinates: {},
            lines: [],
            isLineSelected: false,
            extremePointsOfLine: [],
            strikeThroughLinesForCharacters: [],
            crossLines: [],
            selectedLinesIndexes: [],
            predeterminedNumericSequenceValue: [],
            answerForCountingCombinationsMode: null,
            numOfExamplesForCountingCombinationsGameMode: 0,
        };
    }

    export default {
        mixins: [playBoxGameMixin, EndGameMixin],
        created() {
            window.addEventListener('beforeunload', this.beforeTabClosed);
        },
        updated() {
            if (!this.sequenceOfNumbersTableCells?.length) {
                this.$nextTick(() => {
                    let collectionOfTableCells = document.querySelectorAll('.sequence-of-numbers-table__press-item');
                    this.sequenceOfNumbersTableCells = chunk([...collectionOfTableCells], this.parameters.tableSize);
                });
            }
        },
        beforeDestroy() {
            this.beforeTabClosed();
            window.removeEventListener('beforeunload', this.beforeTabClosed);
        },
        data() {
            return DEFAULT_DATA();
        },
        methods: {
            mouseDown(event, num, rowIndex, columnIndex) {
                if (this.isLineSelected) {
                    this.sequenceOfNumbersTableCells[this.extremePointsOfLine.start[0]][
                        this.extremePointsOfLine.start[1]
                    ].classList.remove('sequence-of-numbers-table__selected-item_red');
                    this.extremePointsOfLine.end = [rowIndex, columnIndex];
                    if (!isEqual(this.extremePointsOfLine?.start, this.extremePointsOfLine?.end)) {
                        this.lines.push({
                            coordinates: this.currentLineCoordinates,
                            extremePointsOfLine: this.extremePointsOfLine,
                            isCorrect: false,
                        });
                        this.setStylesForLine(this.lines.length - 1);
                        this.checkIsAnswerCorrect(this.lines.length - 1);
                    } else {
                        this.removeUnnecessaryLines();
                    }
                    this.extremePointsOfLine.length = 0;
                    this.isLineSelected = false;
                } else {
                    this.sequenceOfNumbersTableCells[rowIndex][columnIndex].classList.add(
                        'sequence-of-numbers-table__selected-item_red'
                    );
                    this.extremePointsOfLine.start = [rowIndex, columnIndex];
                    const { left, right, top, bottom } = event.target.parentNode.getBoundingClientRect();
                    this.currentLineCoordinates.x1 = (left + right) / 2;
                    this.currentLineCoordinates.y1 = (top + bottom) / 2;
                    this.isLineSelected = true;
                }
            },
            mouseMove(event) {
                if (!this.isLineSelected) {
                    return;
                }
                const { left, right, top, bottom } = event.target.parentNode.getBoundingClientRect();
                this.currentLineCoordinates.x2 = (left + right) / 2;
                this.currentLineCoordinates.y2 = (top + bottom) / 2;
                this.drawLines();
            },
            drawLines() {
                this.removeUnnecessaryLines();
                this.drawLine(this.currentLineCoordinates);
            },
            setStylesForLine(lineIndex) {
                const { x1, y1, x2, y2 } = this.lines[lineIndex].coordinates;
                const { width, rotate } = this.getValuesForLineStyles(x1, y1, x2, y2);

                if (!PERMISSIBLE_ANGLES_OF_ROTATION.includes(rotate)) {
                    return;
                }

                this.lines[lineIndex].styles = this.getStyles(x1, y1, width, rotate);
            },
            getStyles(x1, y1, width, rotate) {
                let scroll = pageYOffset;
                let borderWidth = this.isMobile || this.parameters.tableSize > 8 ? 1 : 2;
                return {
                    position: 'absolute',
                    border: `${borderWidth}px solid hsl(14, 100%, 64%)`,
                    left: `${x1}px`,
                    top: `${y1 + scroll}px`,
                    width: `${width}px`,
                    'transform-origin': 'top left',
                    transform: `rotate(${rotate})`,
                };
            },
            drawLine(lineCoordinates) {
                const lineNode = document.createElement('div');
                const { x1, y1, x2, y2 } = lineCoordinates;
                const { width, rotate } = this.getValuesForLineStyles(x1, y1, x2, y2);

                if (!PERMISSIBLE_ANGLES_OF_ROTATION.includes(rotate)) {
                    return;
                }

                Object.assign(lineNode.style, this.getStyles(x1, y1, width, rotate));

                this.$refs.lines.appendChild(lineNode);
            },
            getValuesForLineStyles(x1, y1, x2, y2) {
                const dX = x2 - x1;
                const dY = y2 - y1;
                const width = Math.sqrt(dX ** 2 + dY ** 2);
                const rotate = `${Math.round((Math.atan2(dY, dX) * 180) / Math.PI / 5) * 5}deg`;
                return { width, rotate };
            },
            fetchData() {
                this.getExercise()
                    .then(() => {
                        this.assignDataFromParameters();
                        this.$createjs.Sound.removeAllSounds();
                        this.$createjs.Sound.removeAllEventListeners();
                        this.$createjs.Sound.alternateExtensions = ['mp3', 'ogg'];
                        this.$createjs.Sound.registerSounds(this.soundObjects);
                        this.beforeStart();
                        this.isLoading = false;
                    })
                    .catch(() => {
                        this.isLoading = false;
                        this.$toasted.error(this.$root.$t('errors.getting_level'));
                        this.$router.push({ name: 'dashboard' });
                    });
            },
            beforeStart() {
                this.prepareForGame = false;
                this.gameStarted = true;
                if (!this.timer) {
                    this.setTimer();
                }
            },
            assignDataFromParameters() {
                this.amountOfNumbers = this.parameters.tableSize ** 2;
                this.sequenceOfNumbersTable = Array().fill([]);
                this.sequenceOfNumbersTable = this.generateTable();

                this.horizontallyNumericSequencesSettings.maxCount = this.verticallyNumericSequencesSettings.maxCount = this.parameters.tableSize;

                this.diagonallyNumericSequencesSettings.left.maxCount = this.diagonallyNumericSequencesSettings.right.maxCount =
                    (this.parameters.tableSize - this.parameters.digitCapacity + 1) * 2 - 1;

                if (this.isCountingCombinationsGameMode && this.parameters.crossing) {
                    this.generateSequencesForCountingCombinationsGameModeWithCrossing();
                } else {
                    this.recalculateAvailableCount();
                    this.generateNumericSequences();
                }

                if (!this.timer && !this.prepareForGame) {
                    this.setTimer();
                }

                if (this.answerDuration) {
                    this.startAnswerTimer();
                }
            },
            generateTable() {
                let tableSideLength = this.parameters.tableSize;
                let arrayOfNums = new Array(this.amountOfNumbers);

                for (let index = 0; index < this.amountOfNumbers; index++) {
                    let num = this.generateRandomNumberInAllowedRange(MIN_TABLE_CELL_VALUE, MAX_TABLE_CELL_VALUE + 1);
                    while (arrayOfNums.includes(num) && this.parameters.tableSize === 3) {
                        num = this.generateRandomNumberInAllowedRange(MIN_TABLE_CELL_VALUE, MAX_TABLE_CELL_VALUE + 1);
                    }
                    arrayOfNums[index] = num;
                }

                return this.checkIsTableValid(chunk(arrayOfNums, tableSideLength), tableSideLength);
            },
            checkIsTableValid(table, tableSideLength) {
                for (let i = 0; i < tableSideLength; i++) {
                    for (let j = 0; j < tableSideLength; j++) {
                        let rowIndex = i;
                        let columnIndex = j;

                        let arrayOfNeighbors = [
                            table?.[rowIndex - 1]?.[columnIndex - 1],
                            table?.[rowIndex - 1]?.[columnIndex],
                            table?.[rowIndex - 1]?.[columnIndex + 1],
                            table?.[rowIndex]?.[columnIndex - 1],
                            table?.[rowIndex]?.[columnIndex + 1],
                            table?.[rowIndex + 1]?.[columnIndex - 1],
                            table?.[rowIndex + 1]?.[columnIndex],
                            table?.[rowIndex + 1]?.[columnIndex + 1],
                        ];

                        while (arrayOfNeighbors.includes(table[rowIndex][columnIndex])) {
                            table[rowIndex][columnIndex] = this.generateRandomNumberInAllowedRange(
                                MIN_TABLE_CELL_VALUE,
                                MAX_TABLE_CELL_VALUE + 1
                            );
                        }
                    }
                }

                return table;
            },
            setInaccessibleIndexesForDiagonallyLocation(type, rowIndex, columnIndex, maxIndex, minIndex = 0) {
                const isLeftDiagonallyLocation = type === DIAGONALLY_LOCATION_TYPES[0];
                let [rowIndexCopy, columnIndexCopy] = [rowIndex, columnIndex];
                while (
                    (isLeftDiagonallyLocation && rowIndexCopy < maxIndex && columnIndexCopy < maxIndex) ||
                    (!isLeftDiagonallyLocation && rowIndexCopy >= minIndex && columnIndexCopy < maxIndex)
                ) {
                    this.diagonallyNumericSequencesSettings[`${type}`].uniqueCells.push(
                        this.getCellIndexes(rowIndexCopy, columnIndexCopy)
                    );
                    columnIndexCopy++;
                    isLeftDiagonallyLocation ? rowIndexCopy++ : rowIndexCopy--;
                }
                while (
                    (isLeftDiagonallyLocation && rowIndex > minIndex && columnIndex > minIndex) ||
                    (!isLeftDiagonallyLocation && rowIndex < maxIndex - 1 && columnIndex > minIndex)
                ) {
                    columnIndex--;
                    isLeftDiagonallyLocation ? rowIndex-- : rowIndex++;
                    this.diagonallyNumericSequencesSettings[`${type}`].uniqueCells.push(
                        this.getCellIndexes(rowIndex, columnIndex)
                    );
                }
            },
            generateNumericSequences() {
                for (let numberOfSequence = 1; numberOfSequence <= this.numberOfExamples; numberOfSequence++) {
                    const freeLocations = this.getFreeLocationsForGenerate();
                    const randomIndex = this.generateRandomNumberInAllowedRange(0, freeLocations.length);
                    const location = freeLocations[randomIndex];

                    if (location === DIAGONALLY_LOCATION) {
                        if (
                            this.diagonallyNumericSequencesSettings.left.availableCount > 0 &&
                            this.diagonallyNumericSequencesSettings.right.availableCount > 0
                        ) {
                            this.generateDiagonallyNumericSequences();
                        } else if (this.diagonallyNumericSequencesSettings.left.availableCount > 0) {
                            this.generateDiagonallyNumericSequences(DIAGONALLY_LOCATION_TYPES[0]);
                        } else {
                            this.generateDiagonallyNumericSequences(DIAGONALLY_LOCATION_TYPES[1]);
                        }
                    } else if (location) {
                        this.generateHorizontallyOrVerticallyNumericSequence(location);
                    } else {
                        break;
                    }
                }
                if (!this.parameters.crossing && !this.isCountingCombinationsGameMode) {
                    this.checkIsTableAndSequencesValidForGameModeWithoutCrossing();
                }
                if (this.isCountingCombinationsGameMode) {
                    this.generatePredeterminedNumericSequenceForCountingCombinationsGameMode();
                    this.replaceValuesInTableAccordingToNumericSequencesForCountingCombinationsGameMode();
                    this.checkIsTableAndSequencesValidForGameModeWithoutCrossingForCountingCombinationsGameMode();
                    this.setNumberOfExamplesForCountingCombinationsMode();
                }
            },
            checkIsSequencesUnique(index) {
                let numOfSequenceFounded = 0;

                for (let i = 0; i <= index; i++) {
                    numOfSequenceFounded += this.getNumberOfOccurrencesOfSequenceInTable(
                        this.numericSequencesValues[i]
                    );
                }

                return numOfSequenceFounded === index + 1;
            },
            generateRandomNumericSequence() {
                let maxSequenceValue = 10 ** this.parameters.digitCapacity - 1;
                let randomNumericSequence = Array.from(
                    this.generateRandomNumberInAllowedRange(0, maxSequenceValue).toString()
                ).map(num => +num);
                while (randomNumericSequence.length < this.parameters.digitCapacity) {
                    randomNumericSequence.unshift(0);
                }

                while (
                    isEqual(randomNumericSequence, this.getReversedArray(randomNumericSequence)) ||
                    this.checkIsNumericSequenceIncorrect(randomNumericSequence)
                ) {
                    randomNumericSequence = Array.from(
                        this.generateRandomNumberInAllowedRange(0, maxSequenceValue).toString()
                    ).map(num => +num);
                    while (randomNumericSequence.length < this.parameters.digitCapacity) {
                        randomNumericSequence.unshift(0);
                    }
                }

                return randomNumericSequence;
            },
            checkIsNumericSequenceIncorrect(numericSequence) {
                for (let i = 0; i < numericSequence.length - 1; i++) {
                    if (numericSequence[i] === numericSequence[i + 1]) {
                        return true;
                    }
                }
                return false;
            },
            checkIsTableAndSequencesValidForGameModeWithoutCrossingForCountingCombinationsGameMode() {
                let retriesCounter = 0;
                while (
                    this.getNumberOfOccurrencesOfSequenceInTable(this.predeterminedNumericSequenceValue) !==
                    this.numericSequences.length
                ) {
                    retriesCounter++;
                    if (retriesCounter > MAX_RETRIES_COUNT) {
                        this.numericSequences.pop();
                        this.numericSequencesValues.pop();
                        retriesCounter = 0;
                    }
                    this.generatePredeterminedNumericSequenceForCountingCombinationsGameMode();
                    this.replaceValuesInTableAccordingToNumericSequencesForCountingCombinationsGameMode();
                }
            },
            checkIsTableAndSequencesValidForGameModeWithoutCrossing() {
                for (let i = 0; i < this.numericSequences.length; i++) {
                    let searchedSequence = this.numericSequencesValues[i];
                    let numberOfOccurrencesOfSequenceInTable = this.getNumberOfOccurrencesOfSequenceInTable(
                        searchedSequence
                    );

                    searchedSequence = this.generateRandomNumericSequence();

                    let previousSearchedSequence = searchedSequence;

                    while (numberOfOccurrencesOfSequenceInTable > 1 || !this.checkIsSequencesUnique(i)) {
                        previousSearchedSequence = searchedSequence;
                        numberOfOccurrencesOfSequenceInTable =
                            this.getNumberOfOccurrencesOfSequenceInTable(searchedSequence) + 1;
                        searchedSequence = this.generateRandomNumericSequence();

                        this.numericSequences[i].forEach((cell, index) => {
                            this.numericSequencesValues[i][index] = previousSearchedSequence[index];
                            this.sequenceOfNumbersTable[cell.rowIndex][cell.columnIndex] =
                                previousSearchedSequence[index];
                        });
                    }
                }
            },
            checkIsSequenceAvailableForCountingCombinationsGameMode(sequenceIndexes, currentCell) {
                return !sequenceIndexes.some(({ rowIndex, columnIndex }) => {
                    return this.unavailableCells.some(
                        cell =>
                            cell.columnIndex === columnIndex &&
                            cell.rowIndex === rowIndex &&
                            cell.columnIndex !== currentCell.cell.columnIndex &&
                            cell.rowIndex !== currentCell.cell.rowIndex
                    );
                });
            },
            generatePredeterminedNumericSequenceForCountingCombinationsGameMode() {
                this.predeterminedNumericSequenceValue.length = 0;
                for (let i = 0; i < this.parameters.digitCapacity; i++) {
                    let cellValue = this.generateRandomNumberInAllowedRange(
                        MIN_TABLE_CELL_VALUE,
                        MAX_TABLE_CELL_VALUE + 1
                    );

                    while (this.predeterminedNumericSequenceValue.includes(cellValue)) {
                        cellValue = this.generateRandomNumberInAllowedRange(
                            MIN_TABLE_CELL_VALUE,
                            MAX_TABLE_CELL_VALUE + 1
                        );
                    }

                    this.predeterminedNumericSequenceValue.push(cellValue);
                }
            },
            generateSequencesForCountingCombinationsGameModeWithCrossing() {
                this.generatePredeterminedNumericSequenceForCountingCombinationsGameMode();

                let locationType = this.availableLocations[
                    this.generateRandomNumberInAllowedRange(0, this.availableLocations.length)
                ];

                if (locationType === DIAGONALLY_LOCATION) {
                    let {
                        arrayOfNumericSequenceIndexes,
                        arrayOfNumericSequenceValues,
                        sideDirection,
                    } = this.generateDiagonallyNumericSequences('', undefined, undefined, true);

                    this.setDiagonalNumericSequenceForCountingCombinationsGameMode(
                        sideDirection,
                        arrayOfNumericSequenceIndexes[0].rowIndex,
                        arrayOfNumericSequenceIndexes[0].columnIndex,
                        arrayOfNumericSequenceValues,
                        arrayOfNumericSequenceIndexes
                    );
                } else {
                    let {
                        arrayOfNumericSequenceIndexes,
                        arrayOfNumericSequenceValues,
                    } = this.generateHorizontallyOrVerticallyNumericSequence(locationType, undefined, undefined, true);
                    this.setHorizontallyOrVerticallySequenceForCountingCombinationsGameMode(
                        locationType,
                        arrayOfNumericSequenceIndexes[0].rowIndex,
                        arrayOfNumericSequenceIndexes[0].columnIndex,
                        arrayOfNumericSequenceValues,
                        arrayOfNumericSequenceIndexes
                    );
                }

                let numberOfGeneratedExamples = 0;

                while (
                    numberOfGeneratedExamples !== this.numberOfExamples - 1 &&
                    this.availableCellsForCountingGameModeWithCrossing.length !== 0
                ) {
                    let randomCellIndex = this.generateRandomNumberInAllowedRange(
                        0,
                        this.availableCellsForCountingGameModeWithCrossing.length
                    );
                    let currentCell = this.availableCellsForCountingGameModeWithCrossing[randomCellIndex];
                    let randomAvailableForGenerationCellIndex = this.generateRandomNumberInAllowedRange(
                        0,
                        currentCell.availableCells.length
                    );
                    let sequenceForGenerating = currentCell.availableCells[randomAvailableForGenerationCellIndex];

                    if (sequenceForGenerating.type === DIAGONALLY_LOCATION) {
                        let {
                            arrayOfNumericSequenceIndexes,
                            arrayOfNumericSequenceValues,
                            sideDirection,
                        } = this.generateDiagonallyNumericSequences(
                            sequenceForGenerating.sideDirection,
                            sequenceForGenerating.rowIndex,
                            sequenceForGenerating.columnIndex,
                            true
                        );
                        if (
                            this.checkIsSequenceAvailableForCountingCombinationsGameMode(
                                arrayOfNumericSequenceIndexes,
                                currentCell
                            )
                        ) {
                            numberOfGeneratedExamples++;
                            this.setDiagonalNumericSequenceForCountingCombinationsGameMode(
                                sideDirection,
                                arrayOfNumericSequenceIndexes[0].rowIndex,
                                arrayOfNumericSequenceIndexes[0].columnIndex,
                                arrayOfNumericSequenceValues,
                                arrayOfNumericSequenceIndexes
                            );
                        }
                    } else {
                        let {
                            arrayOfNumericSequenceIndexes,
                            arrayOfNumericSequenceValues,
                        } = this.generateHorizontallyOrVerticallyNumericSequence(
                            sequenceForGenerating.type,
                            sequenceForGenerating.rowIndex,
                            sequenceForGenerating.columnIndex,
                            true
                        );
                        if (
                            this.checkIsSequenceAvailableForCountingCombinationsGameMode(
                                arrayOfNumericSequenceIndexes,
                                currentCell
                            )
                        ) {
                            numberOfGeneratedExamples++;

                            this.setHorizontallyOrVerticallySequenceForCountingCombinationsGameMode(
                                sequenceForGenerating.type,
                                arrayOfNumericSequenceIndexes[0].rowIndex,
                                arrayOfNumericSequenceIndexes[0].columnIndex,
                                arrayOfNumericSequenceValues,
                                arrayOfNumericSequenceIndexes
                            );
                        }
                    }
                    this.availableCellsForCountingGameModeWithCrossing = cloneDeep(
                        this.availableCellsForCountingGameModeWithCrossing
                    ).filter(cell => {
                        return !(
                            cell.cell.columnIndex === currentCell.cell.columnIndex &&
                            cell.cell.rowIndex === currentCell.cell.rowIndex
                        );
                    });
                }

                this.replaceValuesInTableAccordingToNumericSequencesForCountingCombinationsGameMode();
                this.checkIsNumericSequencesCorrectForCountingCombinationsGameModeWithCrossing();
                this.setNumberOfExamplesForCountingCombinationsMode();

                if (this.numOfExamplesForCountingCombinationsGameMode < this.numberOfExamples) {
                    let isGenerationAvailable = true;

                    while (isGenerationAvailable) {
                        let { minRow, maxRow, minColumn, maxColumn } = this.getMaxAndMinIndexesOfUnavailableCells();
                        let {
                            availableColumnsForVerticallyNumericSequences,
                            availableRowsForVerticallyNumericSequences,
                            availableColumnsForHorizontallyNumericSequences,
                            availableRowsForHorizontallyNumericSequences,
                        } = this.getAvailableCellsForHorizontalAndVerticalNumericSequencesForCountingCombinationsGameMode(
                            minRow,
                            maxRow,
                            minColumn,
                            maxColumn
                        );

                        isGenerationAvailable =
                            (availableColumnsForVerticallyNumericSequences?.length &&
                                availableRowsForVerticallyNumericSequences?.length) ||
                            (availableColumnsForHorizontallyNumericSequences?.length &&
                                availableRowsForHorizontallyNumericSequences?.length);

                        let availableLocations = [];
                        if (
                            availableColumnsForVerticallyNumericSequences?.length &&
                            availableRowsForVerticallyNumericSequences?.length
                        ) {
                            availableLocations.push(VERTICALLY_LOCATION);
                        }
                        if (
                            availableColumnsForHorizontallyNumericSequences?.length &&
                            availableRowsForHorizontallyNumericSequences?.length
                        ) {
                            availableLocations.push(HORIZONTALLY_LOCATION);
                        }

                        let location =
                            availableLocations[
                                this.generateRandomNumberInAllowedRange(0, this.availableLocations.length)
                            ];

                        if (location) {
                            let columnIndex, rowIndex;
                            if (location === HORIZONTALLY_LOCATION) {
                                columnIndex =
                                    availableColumnsForHorizontallyNumericSequences[
                                        this.generateRandomNumberInAllowedRange(
                                            0,
                                            availableColumnsForHorizontallyNumericSequences.length
                                        )
                                    ];
                                rowIndex =
                                    availableRowsForHorizontallyNumericSequences[
                                        this.generateRandomNumberInAllowedRange(
                                            0,
                                            availableRowsForHorizontallyNumericSequences.length
                                        )
                                    ];
                            } else {
                                columnIndex =
                                    availableColumnsForVerticallyNumericSequences[
                                        this.generateRandomNumberInAllowedRange(
                                            0,
                                            availableColumnsForVerticallyNumericSequences.length
                                        )
                                    ];
                                rowIndex =
                                    availableRowsForVerticallyNumericSequences[
                                        this.generateRandomNumberInAllowedRange(
                                            0,
                                            availableRowsForVerticallyNumericSequences.length
                                        )
                                    ];
                            }

                            let {
                                arrayOfNumericSequenceIndexes,
                                arrayOfNumericSequenceValues,
                            } = this.generateHorizontallyOrVerticallyNumericSequence(
                                location,
                                rowIndex,
                                columnIndex,
                                true
                            );
                            this.setHorizontallyOrVerticallySequenceForCountingCombinationsGameMode(
                                location,
                                rowIndex,
                                columnIndex,
                                arrayOfNumericSequenceValues,
                                arrayOfNumericSequenceIndexes
                            );
                        }
                    }

                    this.replaceValuesInTableAccordingToNumericSequencesForCountingCombinationsGameMode();
                    this.checkIsNumericSequencesCorrectForCountingCombinationsGameModeWithCrossing();
                    this.setNumberOfExamplesForCountingCombinationsMode();
                }
            },
            getAvailableCellsForHorizontalAndVerticalNumericSequencesForCountingCombinationsGameMode(
                minRow,
                maxRow,
                minColumn,
                maxColumn
            ) {
                let availableColumnsForVerticallyNumericSequences = [],
                    availableRowsForVerticallyNumericSequences = [],
                    availableColumnsForHorizontallyNumericSequences = [],
                    availableRowsForHorizontallyNumericSequences = [];

                for (let i = 0; i < this.parameters.tableSize; i++) {
                    if (i < minColumn || i > maxColumn) {
                        availableColumnsForVerticallyNumericSequences.push(i);
                    }
                    if (i <= this.parameters.tableSize - this.parameters.digitCapacity) {
                        availableRowsForVerticallyNumericSequences.push(i);
                        availableColumnsForHorizontallyNumericSequences.push(i);
                    }
                    if (i < minRow || i > maxRow) {
                        availableRowsForHorizontallyNumericSequences.push(i);
                    }
                }

                return {
                    availableColumnsForVerticallyNumericSequences,
                    availableRowsForVerticallyNumericSequences,
                    availableColumnsForHorizontallyNumericSequences,
                    availableRowsForHorizontallyNumericSequences,
                };
            },
            getMaxAndMinIndexesOfUnavailableCells() {
                let maxRow = 0,
                    maxColumn = 0,
                    minRow = this.parameters.tableSize - 1,
                    minColumn = this.parameters.tableSize - 1;

                this.numericSequences.forEach(numericSequence => {
                    numericSequence.forEach(cell => {
                        if (cell.columnIndex > maxColumn) {
                            maxColumn = cell.columnIndex;
                        } else if (cell.columnIndex < minColumn) {
                            minColumn = cell.columnIndex;
                        }
                        if (cell.rowIndex > maxRow) {
                            maxRow = cell.rowIndex;
                        } else if (cell.rowIndex < minRow) {
                            minRow = cell.rowIndex;
                        }
                    });
                });

                return { minRow, maxRow, minColumn, maxColumn };
            },
            getNumberOfOccurrencesOfSequenceInTable(desiredSequence) {
                let allPossibleSequences = this.getAllPossibleSequences();
                let numOfSequenceFounded = 0;

                allPossibleSequences.forEach(sequence => {
                    let position = -1;
                    while ((position = sequence.indexOf(desiredSequence.join(''), position + 1)) !== -1) {
                        numOfSequenceFounded++;
                    }
                    position = -1;
                    while (
                        (position = sequence.indexOf(this.getReversedArray(desiredSequence).join(''), position + 1)) !==
                        -1
                    ) {
                        numOfSequenceFounded++;
                    }
                });

                return numOfSequenceFounded;
            },
            checkIsNumericSequencesCorrectForCountingCombinationsGameModeWithCrossing() {
                let incorrectNumericSequencesIndexes = [];
                this.numericSequencesValues.length = 0;
                this.numericSequences.forEach((numericSequence, numericSequenceIndex) => {
                    let value = [];
                    numericSequence.forEach(numericSequenceCell => {
                        value.push(
                            this.sequenceOfNumbersTable[numericSequenceCell.rowIndex][numericSequenceCell.columnIndex]
                        );
                    });

                    if (
                        isEqual(
                            numericSequence.map(cell => {
                                return this.sequenceOfNumbersTable[cell.rowIndex][cell.columnIndex];
                            }),
                            this.predeterminedNumericSequenceValue
                        )
                    ) {
                        this.numericSequencesValues.push(value);
                    } else {
                        incorrectNumericSequencesIndexes.push(numericSequenceIndex);
                    }
                });
                this.numericSequences = this.numericSequences.filter((numericSequence, index) => {
                    return !incorrectNumericSequencesIndexes.includes(index);
                });
            },
            replaceValuesInTableAccordingToNumericSequencesForCountingCombinationsGameMode() {
                this.numericSequences.forEach(numericSequence => {
                    numericSequence.forEach((numericSequenceCell, index) => {
                        this.sequenceOfNumbersTable[numericSequenceCell.rowIndex][
                            numericSequenceCell.columnIndex
                        ] = this.predeterminedNumericSequenceValue[index];
                    });
                });
            },
            setDiagonalNumericSequenceForCountingCombinationsGameMode(
                sideDirection,
                rowIndex,
                columnIndex,
                arrayOfNumericSequenceValues,
                arrayOfNumericSequenceIndexes
            ) {
                this.setInaccessibleIndexesForDiagonallyLocation(
                    sideDirection,
                    rowIndex,
                    columnIndex,
                    this.parameters.tableSize
                );
                this.numericSequences.push(arrayOfNumericSequenceIndexes);
                this.diagonallyNumericSequencesSettings.unavailableCells.push(...arrayOfNumericSequenceIndexes);
                this.setAvailableCellsForGenerateSequencesForCountingGameMode(
                    DIAGONALLY_LOCATION,
                    arrayOfNumericSequenceIndexes
                );
            },
            setHorizontallyOrVerticallySequenceForCountingCombinationsGameMode(
                locationType,
                rowIndex,
                columnIndex,
                arrayOfNumericSequenceValues,
                arrayOfNumericSequenceIndexes
            ) {
                let isHorizontallyLocation = locationType === HORIZONTALLY_LOCATION;

                this[`${locationType}NumericSequencesSettings`][
                    isHorizontallyLocation ? `uniqueRowIndexes` : `uniqueColumnIndexes`
                ].push(isHorizontallyLocation ? rowIndex : columnIndex);
                this[`${locationType}NumericSequencesSettings`].unavailableCells.push(...arrayOfNumericSequenceIndexes);
                this.numericSequences.push(arrayOfNumericSequenceIndexes);
                this.setAvailableCellsForGenerateSequencesForCountingGameMode(
                    locationType,
                    arrayOfNumericSequenceIndexes
                );
            },
            setNumberOfExamplesForCountingCombinationsMode() {
                this.numOfExamplesForCountingCombinationsGameMode = this.getNumberOfOccurrencesOfSequenceInTable(
                    this.predeterminedNumericSequenceValue
                );
            },
            getAllPossibleSequences() {
                let allPossibleSequences = [];

                if (this.availableLocations.includes(HORIZONTALLY_LOCATION)) {
                    for (let i = 0; i < this.parameters.tableSize; i++) {
                        let sequence = [];
                        for (let j = 0; j < this.parameters.tableSize; j++) {
                            sequence.push(this.sequenceOfNumbersTable[i][j]);
                        }
                        if (sequence.length >= this.parameters.digitCapacity) {
                            allPossibleSequences.push(sequence.join(''));
                        }
                    }
                }

                if (this.availableLocations.includes(VERTICALLY_LOCATION)) {
                    for (let i = 0; i < this.parameters.tableSize; i++) {
                        let sequence = [];
                        for (let j = 0; j < this.parameters.tableSize; j++) {
                            sequence.push(this.sequenceOfNumbersTable[j][i]);
                        }
                        if (sequence.length >= this.parameters.digitCapacity) {
                            allPossibleSequences.push(sequence.join(''));
                        }
                    }
                }
                if (this.availableLocations.includes(DIAGONALLY_LOCATION)) {
                    for (let i = 0; i < this.parameters.tableSize; i++) {
                        let sequence = [];
                        for (let j = 0; j < this.parameters.tableSize; j++) {
                            if (this.sequenceOfNumbersTable?.[i + j]?.[j] !== undefined) {
                                sequence.push(this.sequenceOfNumbersTable[i + j][j]);
                            }
                        }
                        if (sequence.length >= this.parameters.digitCapacity) {
                            allPossibleSequences.push(sequence.join(''));
                        }
                    }
                    for (let i = 1; i < this.parameters.tableSize; i++) {
                        let sequence = [];
                        for (let j = 0; j < this.parameters.tableSize; j++) {
                            if (this.sequenceOfNumbersTable?.[j]?.[i + j] !== undefined) {
                                sequence.push(this.sequenceOfNumbersTable[j][i + j]);
                            }
                        }
                        if (sequence.length >= this.parameters.digitCapacity) {
                            allPossibleSequences.push(sequence.join(''));
                        }
                    }

                    for (let i = this.parameters.tableSize - 1; i >= 0; i--) {
                        let sequence = [];
                        for (let j = 0; j < this.parameters.tableSize; j++) {
                            if (this.sequenceOfNumbersTable?.[i - j]?.[j] !== undefined) {
                                sequence.push(this.sequenceOfNumbersTable[i - j][j]);
                            }
                        }
                        if (sequence.length >= this.parameters.digitCapacity) {
                            allPossibleSequences.push(sequence.join(''));
                        }
                    }
                    for (let i = 0; i < this.parameters.tableSize - 1; i++) {
                        let sequence = [];
                        for (let j = 0; j < this.parameters.tableSize; j++) {
                            if (this.sequenceOfNumbersTable?.[this.parameters.tableSize - j + i]?.[j] !== undefined) {
                                sequence.push(this.sequenceOfNumbersTable[this.parameters.tableSize - j + i][j]);
                            }
                        }
                        if (sequence.length >= this.parameters.digitCapacity) {
                            allPossibleSequences.push(sequence.join(''));
                        }
                    }
                }
                return allPossibleSequences;
            },
            getAvailableCell(type, sideDirection, rowIndex, columnIndex) {
                return {
                    type: type,
                    sideDirection: sideDirection,
                    rowIndex: rowIndex,
                    columnIndex: columnIndex,
                };
            },
            setAvailableCellsForDiagonallyNumericSequencesForCountingCombinationGameMode(
                columnIndex,
                rowIndex,
                i,
                maxRowOrColumnIndex,
                onlyRightDirection = false,
                onlyLeftDirection = false
            ) {
                let availableCells = [];

                if (
                    columnIndex - i >= 0 &&
                    rowIndex - i >= 0 &&
                    columnIndex - i <= maxRowOrColumnIndex &&
                    rowIndex - i <= maxRowOrColumnIndex &&
                    this.availableLocations.includes(DIAGONALLY_LOCATION) &&
                    !onlyRightDirection
                ) {
                    this.diagonallyNumericSequencesSettings.left.availableCellsForCountingGameModeWithCrossing.push(
                        this.getCellIndexes(rowIndex - i, columnIndex - i)
                    );
                    availableCells.push(
                        this.getAvailableCell(
                            DIAGONALLY_LOCATION,
                            DIAGONALLY_LOCATION_TYPES[0],
                            rowIndex - i,
                            columnIndex - i
                        )
                    );
                }

                if (
                    columnIndex - i >= 0 &&
                    rowIndex + i < this.parameters.tableSize &&
                    columnIndex - i <= maxRowOrColumnIndex &&
                    rowIndex + i >= this.parameters.digitCapacity - 1 &&
                    this.availableLocations.includes(DIAGONALLY_LOCATION) &&
                    !onlyLeftDirection
                ) {
                    this.diagonallyNumericSequencesSettings.right.availableCellsForCountingGameModeWithCrossing.push(
                        this.getCellIndexes(rowIndex + i, columnIndex - i)
                    );
                    availableCells.push(
                        this.getAvailableCell(
                            DIAGONALLY_LOCATION,
                            DIAGONALLY_LOCATION_TYPES[1],
                            rowIndex + i,
                            columnIndex - i
                        )
                    );
                }

                return availableCells;
            },
            setAvailableCellsForHorizontallyNumericSequencesForCountingCombinationGameMode(
                columnIndex,
                rowIndex,
                i,
                maxRowOrColumnIndex
            ) {
                let availableCells = [];

                if (
                    columnIndex - i >= 0 &&
                    columnIndex - i <= maxRowOrColumnIndex &&
                    this.availableLocations.includes(HORIZONTALLY_LOCATION)
                ) {
                    this.horizontallyNumericSequencesSettings.availableCellsForCountingGameModeWithCrossing.push(
                        this.getCellIndexes(rowIndex, columnIndex - i)
                    );
                    availableCells.push(this.getAvailableCell(HORIZONTALLY_LOCATION, null, rowIndex, columnIndex - i));
                }

                return availableCells;
            },
            setAvailableCellsForVerticallyNumericSequencesForCountingCombinationGameMode(
                columnIndex,
                rowIndex,
                i,
                maxRowOrColumnIndex
            ) {
                let availableCells = [];

                if (
                    rowIndex - i >= 0 &&
                    rowIndex - i <= maxRowOrColumnIndex &&
                    this.availableLocations.includes(VERTICALLY_LOCATION)
                ) {
                    this.verticallyNumericSequencesSettings.availableCellsForCountingGameModeWithCrossing.push(
                        this.getCellIndexes(rowIndex - i, columnIndex)
                    );
                    availableCells.push(this.getAvailableCell(VERTICALLY_LOCATION, null, rowIndex - i, columnIndex));
                }

                return availableCells;
            },
            setAvailableCellsForGenerateSequencesForCountingGameMode(type, generatedSequenceCells) {
                const maxRowOrColumnIndex = this.parameters.tableSize - this.parameters.digitCapacity;

                for (let i = 0; i < generatedSequenceCells.length; i++) {
                    let rowIndex = generatedSequenceCells[i].rowIndex;
                    let columnIndex = generatedSequenceCells[i].columnIndex;
                    let availableCells = [];

                    if (type !== HORIZONTALLY_LOCATION) {
                        availableCells.push(
                            ...this.setAvailableCellsForHorizontallyNumericSequencesForCountingCombinationGameMode(
                                columnIndex,
                                rowIndex,
                                i,
                                maxRowOrColumnIndex
                            )
                        );
                    }

                    if (type !== VERTICALLY_LOCATION) {
                        availableCells.push(
                            ...this.setAvailableCellsForVerticallyNumericSequencesForCountingCombinationGameMode(
                                columnIndex,
                                rowIndex,
                                i,
                                maxRowOrColumnIndex
                            )
                        );
                    }

                    if (type !== DIAGONALLY_LOCATION) {
                        availableCells.push(
                            ...this.setAvailableCellsForDiagonallyNumericSequencesForCountingCombinationGameMode(
                                columnIndex,
                                rowIndex,
                                i,
                                maxRowOrColumnIndex
                            )
                        );
                    } else {
                        if (
                            generatedSequenceCells[0].rowIndex <
                            generatedSequenceCells[generatedSequenceCells.length - 1].rowIndex
                        ) {
                            availableCells.push(
                                ...this.setAvailableCellsForDiagonallyNumericSequencesForCountingCombinationGameMode(
                                    columnIndex,
                                    rowIndex,
                                    i,
                                    maxRowOrColumnIndex,
                                    true,
                                    false
                                )
                            );
                        } else {
                            availableCells.push(
                                ...this.setAvailableCellsForDiagonallyNumericSequencesForCountingCombinationGameMode(
                                    columnIndex,
                                    rowIndex,
                                    i,
                                    maxRowOrColumnIndex,
                                    false,
                                    true
                                )
                            );
                        }
                    }

                    if (availableCells?.length) {
                        this.availableCellsForCountingGameModeWithCrossing.push({
                            cell: {
                                rowIndex: rowIndex,
                                columnIndex: columnIndex,
                            },
                            availableCells: availableCells,
                        });
                    }
                }
            },
            getFreeLocationsForGenerate() {
                const isSuitableAmount = ({ availableCount, right, left }) => {
                    if (right && left) {
                        return isSuitableAmount(right) || isSuitableAmount(left);
                    }
                    return availableCount > 0;
                };

                return this.availableLocations.filter(location => {
                    return (
                        (location === DIAGONALLY_LOCATION &&
                            isSuitableAmount(this.diagonallyNumericSequencesSettings)) ||
                        (location === VERTICALLY_LOCATION &&
                            isSuitableAmount(this.verticallyNumericSequencesSettings)) ||
                        (location === HORIZONTALLY_LOCATION &&
                            isSuitableAmount(this.horizontallyNumericSequencesSettings))
                    );
                });
            },
            generateHorizontallyOrVerticallyNumericSequence(
                sequenceType,
                cellRowIndex,
                cellColumnIndex,
                isCountingCombinationsGameModeWithCrossing = false
            ) {
                const maxRowOrColumnIndex = this.parameters.tableSize - this.parameters.digitCapacity,
                    isHorizontallyLocation = sequenceType === HORIZONTALLY_LOCATION;

                let arrayOfNumericSequenceIndexes = [],
                    arrayOfNumericSequenceValues = [],
                    columnIndex,
                    randRowIndex,
                    randColumnIndex,
                    rowIndex;

                do {
                    arrayOfNumericSequenceIndexes.length = 0;
                    arrayOfNumericSequenceValues.length = 0;
                    if (cellRowIndex !== undefined && cellColumnIndex !== undefined) {
                        randColumnIndex = columnIndex = cellColumnIndex;
                        randRowIndex = rowIndex = cellRowIndex;
                    } else {
                        randColumnIndex = columnIndex = isHorizontallyLocation
                            ? this.generateRandomNumberInAllowedRange(0, maxRowOrColumnIndex + 1)
                            : this.generateRandomNumberInAllowedRange(0, this.parameters.tableSize);
                        randRowIndex = rowIndex = isHorizontallyLocation
                            ? this.generateRandomNumberInAllowedRange(0, this.parameters.tableSize)
                            : this.generateRandomNumberInAllowedRange(0, maxRowOrColumnIndex + 1);
                    }

                    for (let i = 0; i < this.parameters.digitCapacity; i++) {
                        arrayOfNumericSequenceIndexes.push(this.getCellIndexes(rowIndex, columnIndex));
                        if (!isCountingCombinationsGameModeWithCrossing) {
                            arrayOfNumericSequenceValues.push(this.sequenceOfNumbersTable[rowIndex][columnIndex]);
                        }
                        isHorizontallyLocation ? columnIndex++ : rowIndex++;
                    }
                } while (
                    (this[`${sequenceType}NumericSequencesSettings`][
                        isHorizontallyLocation ? `uniqueRowIndexes` : `uniqueColumnIndexes`
                    ].includes(isHorizontallyLocation ? randRowIndex : randColumnIndex) ||
                        this.checkIsNumericSequenceExists(arrayOfNumericSequenceValues) ||
                        this.checkIsNumericSequenceUnavailable(arrayOfNumericSequenceIndexes)) &&
                    !isCountingCombinationsGameModeWithCrossing
                );

                if (!isCountingCombinationsGameModeWithCrossing) {
                    if (!this.parameters.crossing) {
                        this[`${sequenceType}NumericSequencesSettings`].unavailableCells.push(
                            ...arrayOfNumericSequenceIndexes
                        );
                    }
                    this[`${sequenceType}NumericSequencesSettings`][
                        isHorizontallyLocation ? `uniqueRowIndexes` : `uniqueColumnIndexes`
                    ].push(isHorizontallyLocation ? randRowIndex : randColumnIndex);
                    this[`${sequenceType}NumericSequencesSettings`].amount += 1;
                    this.numericSequencesValues.push(arrayOfNumericSequenceValues);
                    this.numericSequences.push(arrayOfNumericSequenceIndexes);
                    this.recalculateAvailableCount();
                } else {
                    return { arrayOfNumericSequenceIndexes, arrayOfNumericSequenceValues };
                }
            },
            generateDiagonallyNumericSequences(
                typeOfDiagonallyLocation,
                cellRowIndex,
                cellColumnIndex,
                isCountingCombinationsGameModeWithCrossing = false
            ) {
                let randIndexForTypeOfLocation, isLeftDiagonallyLocation, sideDirection;
                if (typeOfDiagonallyLocation) {
                    isLeftDiagonallyLocation = typeOfDiagonallyLocation === DIAGONALLY_LOCATION_TYPES[0];
                    sideDirection = typeOfDiagonallyLocation;
                } else {
                    randIndexForTypeOfLocation = this.generateRandomNumberInAllowedRange(
                        0,
                        DIAGONALLY_LOCATION_TYPES.length
                    );
                    isLeftDiagonallyLocation =
                        DIAGONALLY_LOCATION_TYPES[randIndexForTypeOfLocation] === DIAGONALLY_LOCATION_TYPES[0];
                    sideDirection = isLeftDiagonallyLocation
                        ? DIAGONALLY_LOCATION_TYPES[0]
                        : DIAGONALLY_LOCATION_TYPES[1];
                }
                let arrayOfNumericSequenceIndexes = [],
                    arrayOfNumericSequenceValues = [],
                    maxRowOrColumnIndex = this.parameters.tableSize - this.parameters.digitCapacity,
                    randRowIndex,
                    randColumnIndex,
                    columnIndex,
                    rowIndex;

                do {
                    arrayOfNumericSequenceIndexes.length = 0;
                    arrayOfNumericSequenceValues.length = 0;

                    if (cellRowIndex !== undefined && cellColumnIndex !== undefined) {
                        randRowIndex = rowIndex = cellRowIndex;
                        randColumnIndex = columnIndex = cellColumnIndex;
                    } else {
                        randRowIndex = rowIndex = isLeftDiagonallyLocation
                            ? this.generateRandomNumberInAllowedRange(0, maxRowOrColumnIndex + 1)
                            : this.generateRandomNumberInAllowedRange(
                                  this.parameters.digitCapacity - 1,
                                  this.parameters.tableSize
                              );
                        randColumnIndex = columnIndex = this.generateRandomNumberInAllowedRange(
                            0,
                            maxRowOrColumnIndex + 1
                        );
                    }

                    for (let i = 1; i <= this.parameters.digitCapacity; i++) {
                        arrayOfNumericSequenceIndexes.push(this.getCellIndexes(randRowIndex, randColumnIndex));
                        if (!isCountingCombinationsGameModeWithCrossing) {
                            arrayOfNumericSequenceValues.push(
                                this.sequenceOfNumbersTable[randRowIndex][randColumnIndex]
                            );
                        }
                        randColumnIndex++;
                        isLeftDiagonallyLocation ? randRowIndex++ : randRowIndex--;
                    }
                } while (
                    (this.diagonallyNumericSequencesSettings[`${sideDirection}`].uniqueCells.some(
                        cell => cell.columnIndex === columnIndex && cell.rowIndex === rowIndex
                    ) ||
                        this.checkIsNumericSequenceExists(arrayOfNumericSequenceValues) ||
                        this.checkIsNumericSequenceUnavailable(arrayOfNumericSequenceIndexes)) &&
                    !isCountingCombinationsGameModeWithCrossing
                );

                if (!isCountingCombinationsGameModeWithCrossing) {
                    this.setInaccessibleIndexesForDiagonallyLocation(
                        sideDirection,
                        rowIndex,
                        columnIndex,
                        this.parameters.tableSize
                    );
                    this.diagonallyNumericSequencesSettings[`${sideDirection}`].amount += 1;
                    if (!this.parameters.crossing) {
                        this.diagonallyNumericSequencesSettings.unavailableCells.push(...arrayOfNumericSequenceIndexes);
                    }
                    this.numericSequencesValues.push(arrayOfNumericSequenceValues);
                    this.numericSequences.push(arrayOfNumericSequenceIndexes);
                    this.recalculateAvailableCount();
                } else {
                    return { arrayOfNumericSequenceIndexes, arrayOfNumericSequenceValues, sideDirection };
                }
            },
            checkIsNumericSequenceUnavailable(indexes) {
                if (this.parameters.crossing) {
                    return false;
                }
                return indexes.some(({ rowIndex, columnIndex }) => {
                    return this.checkIsCellAvailable({ rowIndex, columnIndex });
                });
            },
            checkIsNumericSequenceExists(numericSequence) {
                return this.numericSequencesValues.some(sequence => {
                    return this.areNumericSequencesEqual(sequence, numericSequence);
                });
            },
            checkIsRowContainsAnyGeneratedNumericSequence(numericSequencesValues, row) {
                return numericSequencesValues.some(numericSequenceValue => {
                    return (
                        row.join('').includes(numericSequenceValue) ||
                        this.getReversedArray(row)
                            .join('')
                            .includes(numericSequenceValue)
                    );
                });
            },
            checkIsCellAvailable({ rowIndex, columnIndex }) {
                return this.unavailableCells.some(
                    cell => cell.columnIndex === columnIndex && cell.rowIndex === rowIndex
                );
            },
            getCellIndexes(rowIndex, columnIndex) {
                return { rowIndex, columnIndex };
            },
            recalculateAvailableCount() {
                const numericSequencesValuesAsString = this.numericSequencesValues.map(value => {
                    return value.join('');
                });

                let availableHorizontalCount = 0;

                for (let i = 0; i < this.parameters.tableSize; i++) {
                    let maxAvailableSequence = 0;
                    let maxAvailableSequenceValue = [];

                    for (let j = 0; j < this.parameters.tableSize; j++) {
                        maxAvailableSequence++;
                        maxAvailableSequenceValue.push(this.sequenceOfNumbersTable[i][j]);
                        if (
                            this.checkIsCellAvailable({ rowIndex: i, columnIndex: j }) ||
                            (this.horizontallyNumericSequencesSettings.uniqueRowIndexes.includes(i) &&
                                !this.parameters.crossing)
                        ) {
                            maxAvailableSequence = 0;
                        }
                        if (!this.parameters.crossing && maxAvailableSequence >= this.parameters.digitCapacity) {
                            if (
                                !this.checkIsRowContainsAnyGeneratedNumericSequence(
                                    numericSequencesValuesAsString,
                                    maxAvailableSequenceValue
                                )
                            ) {
                                availableHorizontalCount++;
                            }
                            maxAvailableSequence = 0;
                        }
                        if (
                            this.parameters.crossing &&
                            maxAvailableSequenceValue.length === this.parameters.tableSize
                        ) {
                            if (
                                !this.checkIsRowContainsAnyGeneratedNumericSequence(
                                    numericSequencesValuesAsString,
                                    maxAvailableSequenceValue
                                )
                            ) {
                                availableHorizontalCount++;
                            }
                        }
                    }
                    maxAvailableSequence = 0;
                }
                this.horizontallyNumericSequencesSettings.availableCount = availableHorizontalCount;

                let availableVerticalCount = 0;

                for (let i = 0; i < this.parameters.tableSize; i++) {
                    let maxAvailableSequence = 0;
                    let maxAvailableSequenceValue = [];

                    for (let j = 0; j < this.parameters.tableSize; j++) {
                        maxAvailableSequence++;
                        maxAvailableSequenceValue.push(this.sequenceOfNumbersTable[j][i]);
                        if (
                            this.checkIsCellAvailable({ rowIndex: j, columnIndex: i }) ||
                            (this.verticallyNumericSequencesSettings.uniqueColumnIndexes.includes(i) &&
                                !this.parameters.crossing)
                        ) {
                            maxAvailableSequence = 0;
                        }
                        if (!this.parameters.crossing && maxAvailableSequence >= this.parameters.digitCapacity) {
                            if (
                                !this.checkIsRowContainsAnyGeneratedNumericSequence(
                                    numericSequencesValuesAsString,
                                    maxAvailableSequenceValue
                                )
                            ) {
                                availableVerticalCount++;
                            }
                            maxAvailableSequence = 0;
                        }
                        if (
                            this.parameters.crossing &&
                            maxAvailableSequenceValue.length === this.parameters.tableSize
                        ) {
                            if (
                                !this.checkIsRowContainsAnyGeneratedNumericSequence(
                                    numericSequencesValuesAsString,
                                    maxAvailableSequenceValue
                                )
                            ) {
                                availableVerticalCount++;
                            }
                        }
                    }
                    maxAvailableSequence = 0;
                }
                this.verticallyNumericSequencesSettings.availableCount = availableVerticalCount;

                let availableLeftDiagonalCount = 0;

                for (let i = 0; i < this.parameters.tableSize; i++) {
                    let maxAvailableSequence = 0;
                    let maxAvailableSequenceValue = [];

                    for (let j = 0; j < this.parameters.tableSize; j++) {
                        maxAvailableSequence++;
                        if (this.sequenceOfNumbersTable?.[i + j]?.[j] !== undefined) {
                            maxAvailableSequenceValue.push(this.sequenceOfNumbersTable[i + j][j]);
                        }
                        if (
                            this.checkIsCellAvailable({ rowIndex: i + j, columnIndex: j }) ||
                            i + j >= this.parameters.tableSize ||
                            (this.diagonallyNumericSequencesSettings.left.uniqueCells.some(
                                cell => cell.columnIndex === j && cell.rowIndex === i + j
                            ) &&
                                !this.parameters.crossing)
                        ) {
                            maxAvailableSequence = 0;
                        }
                        if (!this.parameters.crossing && maxAvailableSequence >= this.parameters.digitCapacity) {
                            if (
                                !this.checkIsRowContainsAnyGeneratedNumericSequence(
                                    numericSequencesValuesAsString,
                                    maxAvailableSequenceValue
                                )
                            ) {
                                availableLeftDiagonalCount++;
                            }
                            maxAvailableSequence = 0;
                        }
                        if (
                            this.parameters.crossing &&
                            j === this.parameters.tableSize - 1 &&
                            maxAvailableSequenceValue.length >= this.parameters.digitCapacity
                        ) {
                            if (
                                !this.checkIsRowContainsAnyGeneratedNumericSequence(
                                    numericSequencesValuesAsString,
                                    maxAvailableSequenceValue
                                )
                            ) {
                                availableLeftDiagonalCount++;
                            }
                        }
                    }
                }

                for (let i = 1; i < this.parameters.tableSize; i++) {
                    let maxAvailableSequence = 0;
                    let maxAvailableSequenceValue = [];

                    for (let j = 0; j < this.parameters.tableSize; j++) {
                        maxAvailableSequence++;
                        if (this.sequenceOfNumbersTable?.[j]?.[i + j] !== undefined) {
                            maxAvailableSequenceValue.push(this.sequenceOfNumbersTable[j][i + j]);
                        }
                        if (
                            this.checkIsCellAvailable({ rowIndex: j, columnIndex: i + j }) ||
                            i + j >= this.parameters.tableSize ||
                            (this.diagonallyNumericSequencesSettings.left.uniqueCells.some(
                                cell => cell.columnIndex === i + j && cell.rowIndex === j
                            ) &&
                                !this.parameters.crossing)
                        ) {
                            maxAvailableSequence = 0;
                        }
                        if (!this.parameters.crossing && maxAvailableSequence >= this.parameters.digitCapacity) {
                            if (
                                !this.checkIsRowContainsAnyGeneratedNumericSequence(
                                    numericSequencesValuesAsString,
                                    maxAvailableSequenceValue
                                )
                            ) {
                                availableLeftDiagonalCount++;
                            }
                            maxAvailableSequence = 0;
                        }
                        if (
                            this.parameters.crossing &&
                            j === this.parameters.tableSize - 1 &&
                            maxAvailableSequenceValue.length >= this.parameters.digitCapacity
                        ) {
                            if (
                                !this.checkIsRowContainsAnyGeneratedNumericSequence(
                                    numericSequencesValuesAsString,
                                    maxAvailableSequenceValue
                                )
                            ) {
                                availableLeftDiagonalCount++;
                            }
                        }
                    }
                }
                this.diagonallyNumericSequencesSettings.left.availableCount = availableLeftDiagonalCount;

                let availableRightDiagonalCount = 0;
                for (let i = this.parameters.tableSize - 1; i >= 0; i--) {
                    let maxAvailableSequence = 0;
                    let maxAvailableSequenceValue = [];

                    for (let j = 0; j < this.parameters.tableSize; j++) {
                        maxAvailableSequence++;
                        if (this.sequenceOfNumbersTable?.[i - j]?.[j] !== undefined) {
                            maxAvailableSequenceValue.push(this.sequenceOfNumbersTable[i - j][j]);
                        }
                        if (
                            this.checkIsCellAvailable({ rowIndex: i - j, columnIndex: j }) ||
                            i - j < 0 ||
                            (this.diagonallyNumericSequencesSettings.right.uniqueCells.some(
                                cell => cell.columnIndex === j && cell.rowIndex === i - j
                            ) &&
                                !this.parameters.crossing)
                        ) {
                            maxAvailableSequence = 0;
                        }
                        if (!this.parameters.crossing && maxAvailableSequence >= this.parameters.digitCapacity) {
                            if (
                                !this.checkIsRowContainsAnyGeneratedNumericSequence(
                                    numericSequencesValuesAsString,
                                    maxAvailableSequenceValue
                                )
                            ) {
                                availableRightDiagonalCount++;
                            }
                            maxAvailableSequence = 0;
                        }
                        if (
                            this.parameters.crossing &&
                            j === this.parameters.tableSize - 1 &&
                            maxAvailableSequenceValue.length >= this.parameters.digitCapacity
                        ) {
                            if (
                                !this.checkIsRowContainsAnyGeneratedNumericSequence(
                                    numericSequencesValuesAsString,
                                    maxAvailableSequenceValue
                                )
                            ) {
                                availableRightDiagonalCount++;
                            }
                        }
                    }
                }

                for (let i = 0; i < this.parameters.tableSize - 1; i++) {
                    let maxAvailableSequence = 0;
                    let maxAvailableSequenceValue = [];

                    for (let j = 0; j < this.parameters.tableSize; j++) {
                        maxAvailableSequence++;
                        if (this.sequenceOfNumbersTable?.[this.parameters.tableSize - j + i]?.[j] !== undefined) {
                            maxAvailableSequenceValue.push(
                                this.sequenceOfNumbersTable[this.parameters.tableSize - j + i][j]
                            );
                        }
                        if (
                            this.checkIsCellAvailable({
                                rowIndex: this.parameters.tableSize - j + i,
                                columnIndex: j,
                            }) ||
                            this.parameters.tableSize - j + i >= this.parameters.tableSize ||
                            (this.diagonallyNumericSequencesSettings.right.uniqueCells.some(
                                cell => cell.columnIndex === j && cell.rowIndex === this.parameters.tableSize - j + i
                            ) &&
                                !this.parameters.crossing)
                        ) {
                            maxAvailableSequence = 0;
                        }
                        if (!this.parameters.crossing && maxAvailableSequence >= this.parameters.digitCapacity) {
                            if (
                                !this.checkIsRowContainsAnyGeneratedNumericSequence(
                                    numericSequencesValuesAsString,
                                    maxAvailableSequenceValue
                                )
                            ) {
                                availableRightDiagonalCount++;
                            }
                            maxAvailableSequence = 0;
                        }
                        if (
                            this.parameters.crossing &&
                            j === this.parameters.tableSize - 1 &&
                            maxAvailableSequenceValue.length >= this.parameters.digitCapacity
                        ) {
                            if (
                                !this.checkIsRowContainsAnyGeneratedNumericSequence(
                                    numericSequencesValuesAsString,
                                    maxAvailableSequenceValue
                                )
                            ) {
                                availableRightDiagonalCount++;
                            }
                        }
                    }
                }
                this.diagonallyNumericSequencesSettings.right.availableCount = availableRightDiagonalCount;
            },
            getReversedArray(array) {
                return array.slice().reverse();
            },
            areNumericSequencesEqual(firstNumericSequence, secondNumericSequence) {
                return (
                    isEqual(firstNumericSequence, secondNumericSequence) ||
                    isEqual(firstNumericSequence, this.getReversedArray(secondNumericSequence))
                );
            },
            generateRandomNumberInAllowedRange(minValue, maxValue) {
                return Math.floor(Math.random() * (maxValue - minValue) + minValue);
            },
            getLineIndexesAndValues(lineIndex) {
                let result = {
                        values: [],
                        indexes: [],
                    },
                    lineStartIndexes = this.lines[lineIndex].extremePointsOfLine.start,
                    lineEndIndexes = this.lines[lineIndex].extremePointsOfLine.end,
                    typeOfLine = this.getTypeOfLine(
                        lineStartIndexes[0],
                        lineEndIndexes[0],
                        lineStartIndexes[1],
                        lineEndIndexes[1]
                    ),
                    xStart = lineStartIndexes[0],
                    yStart = lineStartIndexes[1];

                const dx = lineEndIndexes[0] - lineStartIndexes[0],
                    dy = lineEndIndexes[1] - lineStartIndexes[1];

                for (; xStart !== lineEndIndexes[0] || yStart !== lineEndIndexes[1]; ) {
                    result.values.push(this.sequenceOfNumbersTable[xStart][yStart]);
                    result.indexes.push([xStart, yStart]);
                    if (xStart !== lineEndIndexes[0]) {
                        xStart += dx / Math.abs(dx);
                    }
                    if (yStart !== lineEndIndexes[1]) {
                        yStart += dy / Math.abs(dy);
                    }
                }
                result.values.push(this.sequenceOfNumbersTable[xStart][yStart]);
                result.indexes.push([xStart, yStart]);
                return { result, typeOfLine };
            },
            checkIsAnswerCorrectForCountingCombinationsGameMode() {
                if (+this.answerForCountingCombinationsMode === this.numOfExamplesForCountingCombinationsGameMode) {
                    this.correctAnswer();
                } else {
                    this.answerForCountingCombinationsMode = null;
                    this.incorrectAnswer();
                }
            },
            checkIsAnswerCorrect(lineIndex) {
                let { result, typeOfLine } = this.getLineIndexesAndValues(lineIndex);

                if (this.checkIsNumericSequenceExists(result.values) && this.availableLocations.includes(typeOfLine)) {
                    if (this.lines.filter(line => this.areNumericSequencesEqual(line.value, result.values)).length) {
                        this.removeUnnecessaryLines();
                        return;
                    }

                    Object.assign(this.lines[lineIndex], { isCorrect: true, value: result.values });
                    if (!this.isCountingCombinationsGameMode) {
                        this.crossOutTheFoundSequence(result);
                    }
                    this.correctAnswer();
                    return;
                }
                this.incorrectAnswer();
            },
            crossOutTheFoundSequence(result) {
                let searchSequences = this.isNumberGameMode
                        ? document.querySelectorAll('.play-box__search-number')
                        : document.querySelectorAll('.play-box__search-characters'),
                    indexOfSearchSequence = this.isNumberGameMode
                        ? Array.from(searchSequences).findIndex(
                              searchSequence =>
                                  searchSequence.innerHTML === result.values.join('') ||
                                  searchSequence.innerHTML === this.getReversedArray(result.values).join('')
                          )
                        : Array.from(searchSequences).findIndex(searchSequence => {
                              let sequenceValues = Array.from(searchSequence.children).map(child => {
                                  return CELLS_ICONS_NAMES.indexOf(child.id);
                              });
                              return this.areNumericSequencesEqual(result.values, sequenceValues);
                          });

                if (this.isNumberGameMode) {
                    searchSequences[indexOfSearchSequence].classList.add('play-box__search-number_correct');
                    result.indexes.forEach(index => {
                        this.sequenceOfNumbersTableCells[index[0]][index[1]].classList.add(
                            'sequence-of-numbers-table__press-item_red'
                        );
                    });
                    return;
                }

                this.selectedLinesIndexes.push(result.indexes);

                const { left, right, top, bottom } = searchSequences[indexOfSearchSequence].getBoundingClientRect();

                let lineCoordinates = {
                    x1: left + 5,
                    y1: (top + bottom) / 2,
                    x2: right - 10,
                    y2: (top + bottom) / 2,
                };

                const { width, rotate } = this.getValuesForLineStyles(
                    lineCoordinates.x1,
                    lineCoordinates.y1,
                    lineCoordinates.x2,
                    lineCoordinates.y2
                );

                if (!PERMISSIBLE_ANGLES_OF_ROTATION.includes(rotate)) {
                    return;
                }

                this.crossLines.push({
                    coordinates: lineCoordinates,
                    styles: this.getStyles(lineCoordinates.x1, lineCoordinates.y1, width, rotate),
                });
            },
            getTypeOfLine(x1, x2, y1, y2) {
                if (x1 === x2) {
                    return HORIZONTALLY_LOCATION;
                }
                if (y1 === y2) {
                    return VERTICALLY_LOCATION;
                }
                return DIAGONALLY_LOCATION;
            },
            removeUnnecessaryLines() {
                Array.from(this.$refs.lines.children).forEach(child => {
                    this.$refs.lines.removeChild(child);
                });
            },
            checkIsSequenceFounded(numericSequence) {
                return this.lines
                    .filter(line => line.isCorrect)
                    .some(correctLine => {
                        return this.areNumericSequencesEqual(correctLine.value, numericSequence);
                    });
            },
            refreshGameStatBeforeSend() {
                this.gameStat.attempts = this.goodAnswers + this.badAnswers;
                this.gameStat.answers = this.goodAnswers;
                this.gameStat.chainOfAttempts = [];
                this.gameStat.percent = this.calculatePercent();
                this.gameStat.isCompleted = this.gameFinished;
                this.gameStat.executionTime = this.time ?? this.timeBeforeDestroy;
                this.gameStat.isCorrect = this.gameFinished;
                this.gameStat.parameters = cloneDeep(this.parameters);
                this.gameStat.parameters.numOfExamples = this.isCountingCombinationsGameMode
                    ? this.numOfExamplesForCountingCombinationsGameMode
                    : this.numericSequencesValues.length;
            },
            calculatePercent() {
                let total = this.goodAnswers + this.badAnswers;
                if (total > 0) {
                    return parseInt((this.goodAnswers / total) * 100);
                }
                return 0;
            },
            beforeGameRestart() {
                Object.assign(this, DEFAULT_DATA());
            },
            beforeTabClosed() {
                if (this.gameStarted && this.user.type === 'student') {
                    this.sendStat();
                }
                return null;
            },
            correctAnswer() {
                this.goodAnswers++;
                this.$createjs.Sound.play('clickCorrect');
                if (this.goodAnswers === this.numericSequencesValues.length || this.isCountingCombinationsGameMode) {
                    this.finishGame();
                }
            },
            incorrectAnswer() {
                !this.isCountingCombinationsGameMode && this.removeUnnecessaryLines();
                this.badAnswers++;
                this.$createjs.Sound.play('clickIncorrect');
            },
            finishGame() {
                !this.isCountingCombinationsGameMode && this.removeUnnecessaryLines();
                this.gameFinished = true;
                this.stopTimer();
                this.sendStat();
                this.soundInstance = this.$createjs.Sound.play('gameDone');
                this.soundInstance.volume = DEFAULT_VOLUME;
            },
        },
        computed: {
            numericSequencesValuesForShow() {
                return this.numericSequencesValues.map(numericSequence => {
                    return this.generateRandomNumberInAllowedRange(0, 2)
                        ? this.getReversedArray(numericSequence)
                        : numericSequence;
                });
            },
            unavailableCells() {
                return [
                    ...this.verticallyNumericSequencesSettings.unavailableCells,
                    ...this.horizontallyNumericSequencesSettings.unavailableCells,
                    ...this.diagonallyNumericSequencesSettings.unavailableCells,
                ];
            },
            isNumberGameMode() {
                return this.parameters.mode === NUMBERS_GAME_MODE;
            },
            isCountingCombinationsGameMode() {
                return this.parameters.countingCombinations;
            },
            searchNumberFontSize() {
                let width;
                if (this.isNumberGameMode) {
                    width = this.parameters.digitCapacity > 3 ? 'calc(30% - 10px)' : 'calc(20% - 10px)';
                } else {
                    width = this.parameters.digitCapacity > 2 ? 'calc(50% - 10px)' : 'calc(33% - 10px)';
                }
                let fontSize = this.isMobile ? 26 : 32;
                return {
                    width: width,
                    'font-size': fontSize * (1 - this.parameters.digitCapacity / 20) + 'px',
                };
            },
            timeExecutionString() {
                return (
                    this.$i18n.t('progress.progress_table_executionTime') +
                    ': ' +
                    this.time +
                    ' ' +
                    this.$i18n.tc('olympiads.olympiads_timeDeclination_seconds', this.time)
                );
            },
            availableLocations() {
                return [VERTICALLY_LOCATION, HORIZONTALLY_LOCATION, DIAGONALLY_LOCATION].filter(
                    location => this.parameters?.location[location]
                );
            },
            availableLocationsForCountingCombinationsGameMode() {
                let locations = [];
                if (this.availableLocations.includes(DIAGONALLY_LOCATION)) {
                    if (
                        this.diagonallyNumericSequencesSettings.left.availableCellsForCountingGameModeWithCrossing
                            .length ||
                        this.diagonallyNumericSequencesSettings.right.availableCellsForCountingGameModeWithCrossing
                            .length
                    ) {
                        locations.push(DIAGONALLY_LOCATION);
                    }
                }
                if (this.availableLocations.includes(VERTICALLY_LOCATION)) {
                    if (this.verticallyNumericSequencesSettings.availableCellsForCountingGameModeWithCrossing.length) {
                        locations.push(VERTICALLY_LOCATION);
                    }
                }
                if (this.availableLocations.includes(HORIZONTALLY_LOCATION)) {
                    if (
                        this.horizontallyNumericSequencesSettings.availableCellsForCountingGameModeWithCrossing.length
                    ) {
                        locations.push(HORIZONTALLY_LOCATION);
                    }
                }
                return locations;
            },
            linesToDraw() {
                let linesCoordinates = this.lines.filter(line => line.isCorrect).map(line => line.coordinates);
                return [...linesCoordinates, this.currentLineCoordinates].filter(
                    line => line.x1 && line.y1 && line.x2 && line.y2
                );
            },
            numberOfExamples() {
                let numOfExamples = 0;
                this.availableLocations.forEach(location => {
                    if (location === DIAGONALLY_LOCATION) {
                        return (numOfExamples +=
                            this[`${location}NumericSequencesSettings`].left.maxCount +
                            this[`${location}NumericSequencesSettings`].right.maxCount);
                    }
                    return (numOfExamples += this[`${location}NumericSequencesSettings`].maxCount);
                });
                if (this.isCountingCombinationsGameMode) {
                    numOfExamples = this.generateRandomNumberInAllowedRange(1, 10);
                    return numOfExamples;
                }
                return numOfExamples < this.parameters.numOfExamples ? numOfExamples : this.parameters.numOfExamples;
            },
            getTitleForSearchSequences() {
                return this.isNumberGameMode
                    ? this.$i18n.t('trainer.trainer_gameFormSetting_sequenceOfNumbersFindNumericSequence')
                    : this.$i18n.t('trainer.trainer_gameFormSetting_sequenceOfNumbersFindCharacterSequence');
            },
        },
        components: {
            SequenceOfNumbersTable,
            Fireworks,
            GameInfo,
            GameHeader,
            ResultImage,
            SequenceOfNumbersTableIcons,
        },
    };
</script>

<style scoped lang="scss">
    @import '@/assets/sass/games/playbox.scss';

    .cell-image {
        height: 100%;
        display: flex;
        align-items: center;
        justify-content: center;
    }

    .game-info {
        padding-right: 0;
    }

    .play-box {
        height: 100%;
    }

    .play-box__game {
        display: flex;
        justify-content: center;
        align-items: stretch;
        flex: 1 1 auto;
    }

    .play-box__example {
        height: 100%;
        display: flex;
        align-items: center;
        flex-direction: column;
        margin: 0 auto;
        @include media($xlg) {
            flex-direction: row;
            justify-content: center;
        }
        width: 100%;
    }

    .play-box__example-info-container {
        display: flex;
        flex-direction: row;
        justify-content: center;
        align-items: center;
        margin-bottom: 30px;
        @include media($xlg) {
            flex-direction: column;
            margin-bottom: 0;
            margin-right: 30px;
        }
        @include media($xl) {
            margin-right: 100px;
        }
    }

    .game-info__container {
        @include media($xlg) {
            order: 3;
        }
    }

    .play-box__search-number-container {
        display: flex;
        flex-direction: row;
        justify-content: space-around;
        align-items: center;
        flex-wrap: wrap;
        max-width: 430px;
        @include media($xlg) {
            margin-right: 0;
            margin-bottom: 16px;
            flex-direction: column;
        }
    }

    .play-box__header {
        width: 190px;
        font-weight: 500;
        font-size: 18px;
        line-height: 26px;
        margin-bottom: 16px;
        text-align: center;
        @include media($sm) {
            font-size: 22px;
            width: 220px;
        }
        @include media($xlg) {
            width: unset;
            order: 1;
        }
    }

    .play-box__search-number_default {
        background-color: $hsl-color-gray-97;
        color: $hsl-color-green-58;
    }

    .play-box__search-number_correct {
        text-decoration: line-through;
        color: $hsl-color-orange-64;
    }

    .play-box__search-number-wrapper {
        background-color: $x-color-white;
        display: flex;
        justify-content: center;
        align-items: center;
        flex-wrap: wrap;
        width: 100%;
        max-width: 500px;
        padding: 10px 0 0 10px;
        @include media($xlg) {
            order: 2;
            margin-bottom: 30px;
        }
    }

    .play-box__search-number {
        border-radius: $border-radius-10;
        display: flex;
        align-items: center;
        justify-content: center;
        margin: 0 5px 5px 0;
        padding: 5px 0;
        @include media($xlg) {
            margin: 0 10px 10px 0;
            padding: 10px 0;
        }
    }

    .play-box__search-characters {
        border-radius: $border-radius-10;
        display: flex;
        align-items: center;
        justify-content: space-around;
        margin: 0 5px 5px 0;
        padding: 5px;
        @include media($xlg) {
            margin: 0 10px 10px 0;
            padding: 10px;
        }
    }

    .predetermined-sequence-wrapper {
        display: flex;
        align-items: center;
        justify-content: space-around;
        background-color: $x-color-white;
        padding: 10px;
        margin-bottom: 10px;
        @include media($md) {
            margin-right: 50px;
        }
        @include media($xlg) {
            margin-right: 0;
        }
    }

    .predetermined-sequence {
        display: flex;
        flex-direction: row;
        background-color: $hsl-color-gray-97;
        padding: 10px;
        border-radius: $border-radius-10;
    }

    .predetermined-sequence-character {
        margin-right: 5px;
    }

    .play-box__end-container {
        display: flex;
        flex-direction: column;
        align-items: center;
        width: 100%;
    }

    .result__buttons {
        align-items: center;
    }

    .play-box__buttons {
        display: flex;
        flex-direction: column;
        @include media($lg) {
            flex-direction: row;
        }
    }

    .play-box__button {
        margin: 20px 0;
        @include media($lg) {
            margin: 0 20px;
        }
    }

    .play-box__button:first-child {
        margin-top: 0;
        @include media($lg) {
            margin-left: 0;
        }
    }

    .play-box__button:last-child {
        margin-bottom: 0;
        @include media($lg) {
            margin-right: 0;
        }
    }

    .play-box__result {
        height: 100%;
        display: flex;
        align-items: center;
        justify-content: center;
        flex-direction: column;
        flex-grow: 5;
    }

    .play-box__reaction {
        display: flex;
        align-items: center;
        justify-content: center;
        text-align: center;
        flex-shrink: 1;
        flex-wrap: wrap;
        width: 100%;
        @include stack-space($space-base);
        flex-direction: row;
        @include media($md) {
            flex-wrap: nowrap;
        }
    }

    .play-box__reaction-side {
        align-self: normal;
        font-weight: 500;
        font-size: 20px;
        width: 50%;
        color: $hsl-color-green-58;
        @include stack-space($space-base);
        @include media($sm) {
            width: 50%;
            font-size: 28px;
            @include stack-space(0);
        }
        @include media($md) {
            width: unset;
            display: flex;
            flex-direction: column;
            justify-content: center;
            height: 100%;
            flex-grow: 1;
            align-self: center;
            font-size: 20px;
        }
        @include media($lg) {
            font-size: 32px;
        }
        @include media($xlg) {
            font-size: 42px;
        }

        &:first-child {
            order: 1;
            @include media($md) {
                order: unset;
            }
        }

        &:last-child {
            order: 1;
            @include media($md) {
                order: unset;
            }
        }
    }

    .play-box__reaction-side--correct {
        color: $hsl-color-green-58;
    }

    .play-box__reaction-side--incorrect {
        color: $hsl-color-red-62;
    }

    .play-box__reaction-image {
        @include stack-space($space-base);
        height: unset;
        order: 3;
        margin: 0 auto;
        @include media($md) {
            order: unset;
            margin: unset;
        }
    }

    .answer {
        display: flex;
        flex-direction: column;
        justify-content: center;
        align-items: center;
        margin: 0 auto;
        @include media($md) {
            flex-direction: row;
            margin: unset;
        }
        @include media($xlg) {
            flex-direction: column;
            margin: 0 auto;
        }
    }

    .answer-box {
        display: flex;
        flex-direction: column;
        justify-content: center;
        align-items: center;
    }

    .answer-label {
        font-weight: 500;
        font-size: 20px;
        line-height: 25px;
        margin-bottom: 5px;
        @include media($xlg) {
            font-size: 32px;
            margin-bottom: 10px;
            line-height: 38px;
        }
    }

    .answer-input {
        text-align: center;
        max-width: 150px;
        font-size: 32px;
        height: 42px;
        font-weight: 400;
        margin-bottom: 0;
        @include media($xlg) {
            max-width: 230px;
            font-size: 50px;
            height: 80px;
            font-weight: 500;
        }
    }

    .answer-button {
        margin-top: 5px;
        margin-bottom: 10px;
        @include media($xlg) {
            margin-top: 10px;
            margin-bottom: 0;
        }
    }
</style>
